// Production API URL - Update this with your Railway deployment URL
// Or set REACT_APP_API_URL environment variable
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface User {
  id: string;
  email: string;
}

export interface Profile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  created_at: string;
  last_login_at: string | null;
  theme_id: string;
  theme_mode?: 'light' | 'dark' | 'system';
  onboarding_completed?: boolean;
}

export interface Session {
  access_token: string;
  refresh_token: string;
}

export interface LoginResponse {
  message: string;
  user: User;
  profile: Profile;
  session: Session;
  background_job_status?: BackgroundJobStatus;
}

export interface RegisterResponse {
  message: string;
  user: User;
  profile: Profile;
  session: Session;
  background_job_status?: BackgroundJobStatus;
}

export interface MeResponse {
  user: User;
  profile: Profile;
  background_job_status?: BackgroundJobStatus;
}

export interface ApiError {
  error: string;
  details?: string;
}

export interface BackgroundJobStatus {
  is_active: boolean;
  active_job_count: number;
}

// AI Recommendation Interface (defined early as it's referenced by Transaction)
export interface TransactionRecommendation {
  id: string;
  transaction_id: string;
  user_id: string;
  recommended_category: string;
  confidence_score: string | null;
  reasoning: string | null;
  created_at: string | null;
}

export interface Transaction {
  id: string;
  user_id: string;
  amount: string;
  merchant_name: string;
  original_description: string | null;
  category: string;
  transaction_type: 'income' | 'expense' | 'transfer';
  transaction_date: string;
  account_id: string;
  source: string;
  plaid_transaction_id: string | null;
  merchant_logo_url: string | null;
  pending: boolean;
  created_at: string | null;
  updated_at: string | null;
  recommendation?: TransactionRecommendation | null;
}

export interface UpdateTransactionRequest {
  amount?: number;
  merchant_name?: string;
  category?: string;
  transaction_type?: 'income' | 'expense' | 'transfer';
  transaction_date?: string;
  account_id?: string;
}

export interface TransactionResponse {
  transaction: Transaction;
  message?: string;
}

export interface TypeCounts {
  all: number;
  expense: number;
  income: number;
  transfer: number;
  uncategorized: number;
}

export interface DailySpendDataPoint {
  day: number;
  this_month: number | null;
  last_month: number;
}

export interface DailySpendResponse {
  expense_data: DailySpendDataPoint[];
  income_data: DailySpendDataPoint[];
}

export interface TransactionListResponse {
  transactions: Transaction[];
  total: number;
  page: number;
  page_size: number;
  type_counts?: TypeCounts;
}

export type ExpenseCategory =
  | 'Restaurants and meals'
  | 'Travel and tourism'
  | 'Taxi and transportation'
  | 'Gas'
  | 'Utilities & Phone'
  | 'Entertainment'
  | 'Groceries'
  | 'Subscriptions'
  | 'Home Expenses'
  | 'Loan and mortgage payments'
  | 'Fitness and health'
  | 'Shopping'
  | 'Bank Fees'
  | 'Car Expenses'
  | 'ATM Withdrawals'
  | 'Other / One-off Expense'
  | 'Pets'
  | 'Personal Care'
  | 'Uncategorized';

export type IncomeCategory =
  | 'Employment Income'
  | 'Self-Employment Income'
  | 'Investment Income'
  | 'Government & Retirement Income'
  | 'Other / One-Time Income';

export type TransactionCategory = ExpenseCategory | IncomeCategory;

export const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  'Restaurants and meals',
  'Travel and tourism',
  'Taxi and transportation',
  'Gas',
  'Utilities & Phone',
  'Entertainment',
  'Groceries',
  'Subscriptions',
  'Home Expenses',
  'Loan and mortgage payments',
  'Fitness and health',
  'Shopping',
  'Bank Fees',
  'Car Expenses',
  'ATM Withdrawals',
  'Other / One-off Expense',
  'Pets',
  'Personal Care',
  'Uncategorized',
];

export const INCOME_CATEGORIES: IncomeCategory[] = [
  'Employment Income',
  'Self-Employment Income',
  'Investment Income',
  'Government & Retirement Income',
  'Other / One-Time Income',
];

export const TRANSACTION_CATEGORIES: TransactionCategory[] = [
  ...EXPENSE_CATEGORIES,
  ...INCOME_CATEGORIES,
];

// Plaid Integration Interfaces

export interface PlaidLinkTokenResponse {
  link_token: string;
  expiration: string;
}

export interface PlaidExchangeTokenRequest {
  public_token: string;
  institution_id: string;
  institution_name: string;
  selected_account_ids?: string[];
}

export interface PlaidIntegration {
  id: string;
  profile_id: string;
  plaid_item_id: string;
  institution_id: string;
  institution_name: string;
  status: 'active' | 'disconnected' | 'error';
  created_at: string | null;
  updated_at: string | null;
  account_mask: string | null;
  account_name: string | null;
  account_type: string | null;
  account_subtype: string | null;
  account_institution_name: string | null;
  categorization_pending: number;
  categorization_processing: number;
  is_categorizing: boolean;
}

export interface PlaidIntegrationResponse {
  integration: PlaidIntegration;
  message?: string;
}

export interface PlaidIntegrationListResponse {
  integrations: PlaidIntegration[];
  total: number;
}

export interface SyncTransactionsResponse {
  message: string;
  job_id: string;
}

// AI Recommendation Interfaces

export interface AICategorizeResponse {
  message: string;
  success_count: number;
  failed_count: number;
  skipped_count: number;
  total_processed: number;
}

export interface RecommendationResponse {
  recommendation: TransactionRecommendation | null;
  message?: string;
}

export interface ApplyRecommendationResponse {
  message: string;
  transaction: Transaction;
}

// Theme Interfaces

export type ThemeCategory = 'vibrant' | 'earthy' | 'nature' | 'professional';
export type ThemeMode = 'light' | 'dark';

export interface Theme {
  id: string;
  name: string;
  description: string;
  category: ThemeCategory;
  mode: ThemeMode;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  background_color: string;
  surface_color: string;
  text_color: string;
  text_secondary_color: string;
  expense_color: string;
  income_color: string;
}

export const THEME_CATEGORIES: { id: ThemeCategory; name: string; description: string; icon: string }[] = [
  { id: 'vibrant', name: 'Vibrant', description: 'Bold and energetic', icon: '✨' },
  { id: 'earthy', name: 'Earthy', description: 'Warm and grounding', icon: '🌾' },
  { id: 'nature', name: 'Alto', description: 'Alto greens', icon: '🟩' },
  { id: 'professional', name: 'Professional', description: 'Clean and minimal', icon: '💼' },
];

export interface ThemesListResponse {
  themes: Theme[];
  total: number;
}

export interface UpdateThemeRequest {
  theme_id: string;
  theme_mode?: 'light' | 'dark' | 'system';
}

// Analytics Interfaces

export interface CategorySpend {
  category: string;
  total: string;
  count: number;
}

export interface MonthlySpend {
  month: string;
  expenses: string;
  income: string;
  expense_count: number;
  income_count: number;
}

export interface MonthlyStats {
  total_transactions: number;
  total_spend: string;
}

export interface MonthlyCategorySpend {
  month: string;
  category: string;
  amount: string;
  count: number;
}

export interface AnalyticsResponse {
  current_month: MonthlyStats;
  spend_by_category: CategorySpend[];
  income_by_category: CategorySpend[];
  monthly_spend: MonthlySpend[];
  monthly_expense_by_category: MonthlyCategorySpend[];
  monthly_income_by_category: MonthlyCategorySpend[];
}

// Account Interfaces

export interface Account {
  id: string;
  user_id: string;
  name: string;
  account_type: 'asset' | 'liability';
  balance: string;
  source: 'plaid';
  plaid_account_id: string | null;
  plaid_integration_id: string | null;
  masked_number: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface UpdateAccountRequest {
  name?: string;
  account_type?: 'asset' | 'liability';
}

export interface AccountResponse {
  account: Account;
  message?: string;
}

export interface AccountSummary {
  total_assets: string;
  total_liabilities: string;
  net_worth: string;
}

export interface AccountListResponse {
  accounts: Account[];
  total: number;
  summary: AccountSummary;
}

// Chat Interfaces

export interface ChatMessage {
  id: string;
  user_id: string;
  role: 'user' | 'assistant';
  content: string;
  tool_calls?: Array<{ name: string; arguments: any }> | null;
  created_at: string | null;
}

export interface ChatHistoryResponse {
  messages: ChatMessage[];
  total: number;
}

// Insight Interfaces

export type InsightType = 
  | 'spending_fluctuation'
  | 'spending_forecast'
  | 'overdraft_alert'
  | 'vendor_increase'
  | 'subscription_increase'
  | 'subscription_spending';

export type InsightSeverity = 'info' | 'warning' | 'alert';

export interface Insight {
  id: string;
  user_id: string;
  insight_type: InsightType;
  title: string;
  message: string;
  metadata: Record<string, any> | null;
  severity: InsightSeverity;
  vote: number | null;
  generated_at: string | null;
  expires_at: string | null;
  created_at: string | null;
}

export interface InsightListResponse {
  insights: Insight[];
  total: number;
}

export interface InsightResponse {
  insight: Insight;
  message?: string;
}

export interface WeeklyInsightsSettings {
  enabled: boolean;
  days: string[];
  send_hour: number;
  timezone: string;
  last_sent_at?: string | null;
}

export interface WeeklyInsightsSettingsResponse {
  settings: WeeklyInsightsSettings;
}

export interface UpdateWeeklyInsightsSettingsRequest {
  enabled: boolean;
  days: string[];
  send_hour: number;
  timezone: string;
}

export interface MonthlyRoundupSettings {
  enabled: boolean;
  send_hour: number;
  timezone: string;
  last_sent_at?: string | null;
}

export interface MonthlyRoundupSettingsResponse {
  settings: MonthlyRoundupSettings;
}

export interface UpdateMonthlyRoundupSettingsRequest {
  enabled: boolean;
  send_hour: number;
  timezone: string;
}

// Weekly spend summary (expenses)
export interface WeeklySpendDelta {
  amount: string;
  percent: number | null;
}

export interface WeeklySpendCategoryRow {
  category: string;
  weekly_totals: string[];
  last_30: string;
  l4w_weekly_average: string;
}

export interface WeeklySpendWeek {
  start: string;
  label: string;
}

export interface WeeklySpendSummary {
  weeks: WeeklySpendWeek[];
  categories: WeeklySpendCategoryRow[];
  current_week_label: string;
}

export interface WeeklySpendSummaryResponse {
  summary: WeeklySpendSummary;
}

// Dashboard types (optimized combined endpoint)
export interface DashboardCurrentMonth {
  total_spending: string;
  total_income: string;
  spending_change_percent: number | null;
  income_change_percent: number | null;
}

export interface DashboardMonthlyData {
  month: string;
  expenses: string;
  income: string;
}

export interface DashboardResponse {
  current_month: DashboardCurrentMonth;
  monthly_trend: DashboardMonthlyData[];
  recent_transactions: Transaction[];
  insights: Insight[];
}

// Shared Plaid integrations cache to prevent duplicate requests across components
let plaidIntegrationsCache: PlaidIntegrationListResponse | null = null;
let plaidIntegrationsInFlight: Promise<PlaidIntegrationListResponse> | null = null;

class ApiService {
  private unauthorizedHandler: (() => void) | null = null;

  setUnauthorizedHandler(handler: () => void) {
    this.unauthorizedHandler = handler;
  }

  clearUnauthorizedHandler() {
    this.unauthorizedHandler = null;
  }

  private getAuthHeader(): Record<string, string> {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: 'An error occurred',
      }));

      if (response.status === 401) {
        this.unauthorizedHandler?.();
        throw new Error(error.error || 'Session expired. Please log in again.');
      }

      throw new Error(error.error || 'An error occurred');
    }
    return response.json();
  }

  // Helper to create fetch with abort signal and timeout
  private async fetchWithSignal(
    url: string,
    options: RequestInit & { signal?: AbortSignal; timeout?: number } = {}
  ): Promise<Response> {
    const { timeout = 30000, signal, ...fetchOptions } = options;
    
    // Create a timeout abort controller
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), timeout);
    
    // Combine the external signal (if any) with the timeout signal
    const combinedSignal = signal 
      ? this.combineAbortSignals(signal, timeoutController.signal)
      : timeoutController.signal;

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        signal: combinedSignal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        // Check if it was a timeout or user cancellation
        if (timeoutController.signal.aborted && (!signal || !signal.aborted)) {
          throw new Error('Request timeout - please check your connection');
        }
        // Re-throw abort error for component cleanup
        throw error;
      }
      throw error;
    }
  }

  // Combine multiple abort signals
  private combineAbortSignals(...signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController();
    for (const signal of signals) {
      if (signal.aborted) {
        controller.abort();
        return controller.signal;
      }
      signal.addEventListener('abort', () => controller.abort(), { once: true });
    }
    return controller.signal;
  }

  async register(
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ): Promise<RegisterResponse> {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      }),
    });
    return this.handleResponse<RegisterResponse>(response);
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });
    return this.handleResponse<LoginResponse>(response);
  }

  async logout(): Promise<void> {
    const response = await fetch(`${API_URL}/api/auth/logout`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
      },
    });
    await this.handleResponse<{ message: string }>(response);
  }

  async getCurrentUser(signal?: AbortSignal): Promise<MeResponse> {
    const response = await this.fetchWithSignal(`${API_URL}/api/auth/me`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
      },
      signal,
    });
    return this.handleResponse<MeResponse>(response);
  }

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/api/auth/reset-password-request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    return this.handleResponse<{ message: string }>(response);
  }

  async resetPassword(
    accessToken: string,
    newPassword: string
  ): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        access_token: accessToken,
        new_password: newPassword,
      }),
    });
    return this.handleResponse<{ message: string }>(response);
  }

  async refreshToken(refreshToken: string): Promise<Session> {
    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    const data = await this.handleResponse<{ session: Session }>(response);
    return data.session;
  }

  // Transaction methods

  async getTransactions(params?: {
    search?: string;
    category?: string;
    transaction_type?: 'income' | 'expense' | 'transfer';
    start_date?: string;
    end_date?: string;
    sort_by?: 'date' | 'amount' | 'merchant' | 'category';
    sort_order?: 'asc' | 'desc';
    page?: number;
    page_size?: number;
    account_id?: string;
    include_recommendations?: boolean;
    include_counts?: boolean;
    signal?: AbortSignal;
  }): Promise<TransactionListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.transaction_type) queryParams.append('transaction_type', params.transaction_type);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.account_id) queryParams.append('account_id', params.account_id);
    if (params?.include_recommendations !== undefined) queryParams.append('include_recommendations', params.include_recommendations.toString());
    if (params?.include_counts) queryParams.append('include_counts', 'true');

    const url = `${API_URL}/api/transactions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await this.fetchWithSignal(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
      },
      signal: params?.signal,
    });
    return this.handleResponse<TransactionListResponse>(response);
  }

  async getTransaction(id: string): Promise<TransactionResponse> {
    const response = await fetch(`${API_URL}/api/transactions/${id}`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
      },
    });
    return this.handleResponse<TransactionResponse>(response);
  }

  async updateTransaction(
    id: string,
    data: UpdateTransactionRequest
  ): Promise<TransactionResponse> {
    const response = await fetch(`${API_URL}/api/transactions/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(data),
    });
    return this.handleResponse<TransactionResponse>(response);
  }

  async deleteTransaction(id: string): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/api/transactions/${id}`, {
      method: 'DELETE',
      headers: {
        ...this.getAuthHeader(),
      },
    });
    return this.handleResponse<{ message: string }>(response);
  }

  async getAnalytics(startDate?: string, endDate?: string, signal?: AbortSignal): Promise<AnalyticsResponse> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('start_date', startDate);
    if (endDate) queryParams.append('end_date', endDate);

    const url = `${API_URL}/api/transactions/analytics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await this.fetchWithSignal(url, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
      },
      signal,
    });
    return this.handleResponse<AnalyticsResponse>(response);
  }

  async getDailySpend(): Promise<DailySpendResponse> {
    const response = await fetch(`${API_URL}/api/transactions/daily-spend`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
      },
    });
    return this.handleResponse<DailySpendResponse>(response);
  }

  // Plaid Integration methods

  async createPlaidLinkToken(): Promise<PlaidLinkTokenResponse> {
    const response = await fetch(`${API_URL}/api/plaid/create_link_token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });
    return this.handleResponse<PlaidLinkTokenResponse>(response);
  }

  async exchangePlaidToken(
    data: PlaidExchangeTokenRequest
  ): Promise<PlaidIntegrationResponse> {
    const response = await fetch(`${API_URL}/api/plaid/exchange_token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(data),
    });
    const result = await this.handleResponse<PlaidIntegrationResponse>(response);
    // Bank connect triggers a background sync; prompt Layout to refresh /me and start polling if needed.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('alto:jobs_changed'));
    }
    return result;
  }

  async getPlaidIntegrations(signal?: AbortSignal): Promise<PlaidIntegrationListResponse> {
    const response = await this.fetchWithSignal(`${API_URL}/api/plaid/integrations`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
      },
      signal,
    });
    return this.handleResponse<PlaidIntegrationListResponse>(response);
  }

  /**
   * Single-flight + cached fetch for Plaid integrations to avoid duplicate calls.
   * Pass force=true after mutating actions (sync/disconnect/link) to refresh data.
   */
  async getPlaidIntegrationsOnce(
    options: { signal?: AbortSignal; force?: boolean } = {}
  ): Promise<PlaidIntegrationListResponse> {
    const { signal, force = false } = options;

    if (!force && plaidIntegrationsCache) {
      return plaidIntegrationsCache;
    }

    if (!force && plaidIntegrationsInFlight) {
      return plaidIntegrationsInFlight;
    }

    const fetchPromise = this.getPlaidIntegrations(signal)
      .then((data) => {
        plaidIntegrationsCache = data;
        plaidIntegrationsInFlight = null;
        return data;
      })
      .catch((err) => {
        plaidIntegrationsInFlight = null;
        throw err;
      });

    if (!force) {
      plaidIntegrationsInFlight = fetchPromise;
    }

    return fetchPromise;
  }

  async syncPlaidTransactions(
    integrationId: string
  ): Promise<SyncTransactionsResponse> {
    const response = await fetch(
      `${API_URL}/api/plaid/sync_transactions/${integrationId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
      }
    );
    const result = await this.handleResponse<SyncTransactionsResponse>(response);
    // Manual sync now enqueues a background job; prompt Layout to refresh /me and start polling if needed.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('alto:jobs_changed'));
    }
    return result;
  }

  async disconnectPlaidIntegration(integrationId: string): Promise<{ message: string }> {
    const response = await fetch(
      `${API_URL}/api/plaid/integrations/${integrationId}`,
      {
        method: 'DELETE',
        headers: {
          ...this.getAuthHeader(),
        },
      }
    );
    return this.handleResponse<{ message: string }>(response);
  }

  async createPlaidUpdateLinkToken(integrationId: string): Promise<PlaidLinkTokenResponse> {
    const response = await fetch(
      `${API_URL}/api/plaid/create_update_link_token/${integrationId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
      }
    );
    return this.handleResponse<PlaidLinkTokenResponse>(response);
  }

  async reconnectPlaidIntegration(integrationId: string): Promise<PlaidIntegrationResponse> {
    const response = await fetch(
      `${API_URL}/api/plaid/reconnect/${integrationId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
      }
    );
    const result = await this.handleResponse<PlaidIntegrationResponse>(response);
    // Reconnect triggers a background sync; prompt Layout to refresh
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('alto:jobs_changed'));
    }
    return result;
  }

  // AI Recommendation methods

  async triggerAICategorization(): Promise<AICategorizeResponse> {
    const response = await fetch(`${API_URL}/api/transactions/ai-categorize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });
    return this.handleResponse<AICategorizeResponse>(response);
  }

  // DEPRECATED: Recommendations are now included directly in transaction responses
  // This method is kept for backward compatibility but is no longer used
  // async getTransactionRecommendation(transactionId: string): Promise<RecommendationResponse> {
  //   const response = await fetch(
  //     `${API_URL}/api/transactions/${transactionId}/recommendation`,
  //     {
  //       method: 'GET',
  //       headers: {
  //         ...this.getAuthHeader(),
  //       },
  //     }
  //   );
  //   return this.handleResponse<RecommendationResponse>(response);
  // }

  async applyRecommendation(transactionId: string): Promise<ApplyRecommendationResponse> {
    const response = await fetch(
      `${API_URL}/api/transactions/${transactionId}/apply-recommendation`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
      }
    );
    return this.handleResponse<ApplyRecommendationResponse>(response);
  }

  // Theme methods

  async getThemes(): Promise<ThemesListResponse> {
    const response = await fetch(`${API_URL}/api/auth/themes`, {
      method: 'GET',
    });
    return this.handleResponse<ThemesListResponse>(response);
  }

  async updateUserTheme(themeId: string, themeMode?: 'light' | 'dark' | 'system'): Promise<MeResponse> {
    const response = await fetch(`${API_URL}/api/auth/profile/theme`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify({ theme_id: themeId, ...(themeMode ? { theme_mode: themeMode } : {}) }),
    });
    return this.handleResponse<MeResponse>(response);
  }

  async updateOnboardingStatus(onboardingCompleted: boolean): Promise<MeResponse> {
    const response = await fetch(`${API_URL}/api/auth/profile/onboarding`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify({ onboarding_completed: onboardingCompleted }),
    });
    return this.handleResponse<MeResponse>(response);
  }

  // Account methods

  async getAccounts(signal?: AbortSignal): Promise<AccountListResponse> {
    const response = await this.fetchWithSignal(`${API_URL}/api/accounts`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
      },
      signal,
    });
    return this.handleResponse<AccountListResponse>(response);
  }

  async getAccount(id: string): Promise<AccountResponse> {
    const response = await fetch(`${API_URL}/api/accounts/${id}`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
      },
    });
    return this.handleResponse<AccountResponse>(response);
  }

  async updateAccount(id: string, data: UpdateAccountRequest): Promise<AccountResponse> {
    const response = await fetch(`${API_URL}/api/accounts/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(data),
    });
    return this.handleResponse<AccountResponse>(response);
  }

  async deleteAccount(id: string): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/api/accounts/${id}`, {
      method: 'DELETE',
      headers: {
        ...this.getAuthHeader(),
      },
    });
    return this.handleResponse<{ message: string }>(response);
  }

  // Chat API methods
  async getChatHistory(signal?: AbortSignal): Promise<ChatHistoryResponse> {
    const response = await this.fetchWithSignal(`${API_URL}/api/chat/history`, {
      method: 'GET',
      headers: this.getAuthHeader(),
      signal,
    });
    return this.handleResponse<ChatHistoryResponse>(response);
  }

  async clearChatHistory(): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/api/chat/history`, {
      method: 'DELETE',
      headers: this.getAuthHeader(),
    });
    return this.handleResponse<{ message: string }>(response);
  }

  async sendChatMessage(
    message: string,
    onChunk: (chunk: any) => void,
    onError?: (error: string) => void,
    onComplete?: () => void
  ): Promise<void> {
    const token = localStorage.getItem('access_token');
    const url = `${API_URL}/api/chat`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ message }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Response body is not readable');
      }

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          onComplete?.();
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            try {
              const parsed = JSON.parse(data);
              onChunk(parsed);
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      onError?.(errorMessage);
      throw error;
    }
  }

  // Insight API methods

  async getInsights(includeVoted: boolean = false, signal?: AbortSignal): Promise<InsightListResponse> {
    const queryParams = new URLSearchParams();
    if (includeVoted) queryParams.append('include_voted', 'true');
    
    const url = `${API_URL}/api/insights${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await this.fetchWithSignal(url, {
      method: 'GET',
      headers: this.getAuthHeader(),
      signal,
    });
    return this.handleResponse<InsightListResponse>(response);
  }

  async getInsight(id: string): Promise<InsightResponse> {
    const response = await fetch(`${API_URL}/api/insights/${id}`, {
      method: 'GET',
      headers: this.getAuthHeader(),
    });
    return this.handleResponse<InsightResponse>(response);
  }

  async voteInsight(id: string, vote: 1 | -1): Promise<InsightResponse> {
    const response = await fetch(`${API_URL}/api/insights/${id}/vote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify({ vote }),
    });
    return this.handleResponse<InsightResponse>(response);
  }

  async getWeeklyInsightsSettings(): Promise<WeeklyInsightsSettingsResponse> {
    const response = await fetch(`${API_URL}/api/insights/weekly-settings`, {
      method: 'GET',
      headers: this.getAuthHeader(),
    });
    return this.handleResponse<WeeklyInsightsSettingsResponse>(response);
  }

  async updateWeeklyInsightsSettings(
    payload: UpdateWeeklyInsightsSettingsRequest
  ): Promise<WeeklyInsightsSettingsResponse> {
    const response = await fetch(`${API_URL}/api/insights/weekly-settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    return this.handleResponse<WeeklyInsightsSettingsResponse>(response);
  }

  async getMonthlyRoundupSettings(): Promise<MonthlyRoundupSettingsResponse> {
    const response = await fetch(`${API_URL}/api/insights/monthly-roundup-settings`, {
      method: 'GET',
      headers: this.getAuthHeader(),
    });
    return this.handleResponse<MonthlyRoundupSettingsResponse>(response);
  }

  async updateMonthlyRoundupSettings(
    payload: UpdateMonthlyRoundupSettingsRequest
  ): Promise<MonthlyRoundupSettingsResponse> {
    const response = await fetch(`${API_URL}/api/insights/monthly-roundup-settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    return this.handleResponse<MonthlyRoundupSettingsResponse>(response);
  }

  async getWeeklySpendSummary(startDate?: string, endDate?: string, signal?: AbortSignal): Promise<WeeklySpendSummaryResponse> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('start_date', startDate);
    if (endDate) queryParams.append('end_date', endDate);

    const url = `${API_URL}/api/insights/weekly-summary${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await this.fetchWithSignal(url, {
      method: 'GET',
      headers: this.getAuthHeader(),
      signal,
    });
    return this.handleResponse<WeeklySpendSummaryResponse>(response);
  }

  // Dashboard API - optimized combined endpoint for home page
  async getDashboard(signal?: AbortSignal): Promise<DashboardResponse> {
    const response = await this.fetchWithSignal(`${API_URL}/api/dashboard`, {
      method: 'GET',
      headers: this.getAuthHeader(),
      signal,
    });
    return this.handleResponse<DashboardResponse>(response);
  }
}

export const apiService = new ApiService();

